/**
 * "My Skill" panel — every active skill/application in the catalog, with
 * the caller's own rating overlaid, plus a "request a new skill" form
 * (opened from its own button, in a popup — not a page tab, so the table
 * is always what you land on). Embedded as a tab on the employee detail
 * page (`/users/:id`, self-view only); previously its own route
 * (`/my-skills`), moved here so it's reachable without `user.view`.
 *
 * Route-agnostic by design: all filter state comes in via props instead of
 * `Route.useSearch()`, so the embedding page owns the URL search schema and
 * this component has no idea it's inside `/users/:id`. The API calls it
 * makes (`myRatedCatalog`/`rateSkill`/skill submissions) are all
 * self-scoped server-side (`/me/skills/...`) — no userId needed.
 *
 * Pass `userId` to show SOMEONE ELSE's ratings instead (a manager viewing a
 * report): the panel then reads `/users/:id/skills/ratings` and is fully
 * read-only — no rating, no certification edits, no skill requests.
 */
import type { MyRatedSkill } from '@clockwise/clients/users';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Loader2, Paperclip, Plus, Star } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { PaginationFooter } from '@/components/custom/pagination-footer';
import { ProficiencyScaleReference } from '@/components/custom/proficiency-scale-reference';
import { SearchInput } from '@/components/custom/search-input';
import {
  CertificateDownloadButton,
  CertificateFileEditor,
  useCertificateFileActions,
} from '@/components/custom/skill-certificate-file';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePageWindow } from '@/hooks/use-page-window';
import { skillSubmissionsApi, usersApi } from '@/lib/api';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/** Category cards per page, and skill rows shown per card before "+N more". */
const CATEGORIES_PER_PAGE = 6;
const ROWS_PER_CATEGORY = 5;

export interface MySkillPanelSearch {
  q?: string;
  category?: string;
  unrated?: boolean;
}

const LEVEL_LABELS: Record<'skill' | 'application', Record<number, string>> = {
  skill: {
    0: 'No experience',
    1: 'Awareness',
    2: 'Basic',
    3: 'Proficient',
    4: 'Advanced',
    5: 'SME',
  },
  application: {
    0: 'No experience',
    1: 'Awareness',
    2: 'Basic User',
    3: 'Proficient',
    4: 'Advanced',
    5: 'SME',
  },
};

/** Never touched — no row saved at all. Distinct from "Not applicable"/"Do
 *  not know", which ARE a completed, conscious choice. */
function isUnrated(row: MyRatedSkill): boolean {
  return row.rating == null;
}

export function MySkillPanel({
  userId,
  search,
  onSearchChange,
}: {
  /** Whose ratings to show, read-only. Omit for the caller's own, editable. */
  userId?: string;
  search: MySkillPanelSearch;
  onSearchChange: (patch: Partial<MySkillPanelSearch>) => void;
}) {
  const readOnly = userId !== undefined;
  // Whose ratings these are — for certificate downloads.
  const { user: me } = useMe();
  const ownerUserId = userId ?? me?.id;
  const queryClient = useQueryClient();
  const [scaleOpen, setScaleOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  const [searchInput, setSearchInput] = useState(search.q ?? '');
  useEffect(() => {
    setSearchInput(search.q ?? '');
  }, [search.q]);
  useEffect(() => {
    if (searchInput === (search.q ?? '')) return;
    const handle = setTimeout(() => onSearchChange({ q: searchInput }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, search.q]);

  const query = useQuery({
    queryKey: userId ? queryKeys.userSkills.of(userId) : queryKeys.userSkills.mine(),
    queryFn: () => (userId ? usersApi.ratedCatalogFor(userId) : usersApi.myRatedCatalog()),
  });
  const rows = query.data?.data ?? [];

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (r.skill.category) set.add(r.skill.category);
    }
    return Array.from(set).sort();
  }, [rows]);

  const q = (search.q ?? '').trim().toLowerCase();
  const category = search.category;
  const unratedOnly = search.unrated ?? false;
  const filtered = rows.filter((r) => {
    if (q && !r.skill.name.toLowerCase().includes(q)) return false;
    if (category && r.skill.category !== category) return false;
    if (unratedOnly && !isUnrated(r)) return false;
    return true;
  });

  const grouped = useMemo(() => {
    const byCategory = new Map<string, typeof filtered>();
    for (const r of filtered) {
      const key = r.skill.category ?? 'Uncategorized';
      const list = byCategory.get(key) ?? [];
      list.push(r);
      byCategory.set(key, list);
    }
    return Array.from(byCategory.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // Category CARDS are paginated (not one long scrolling page of every
  // category) — any filter change resets to the first page, same pattern
  // customer-team-panel.tsx uses: a search on page 4 should land you on
  // page 1 of the new result set, not strand you on a now-empty page.
  const [catPage, setCatPage] = useState(0);
  useEffect(() => {
    setCatPage(0);
  }, [search.q, category, unratedOnly]);
  const { pageCount, shownOffset } = usePageWindow({
    page: catPage,
    setPage: setCatPage,
    pageSize: CATEGORIES_PER_PAGE,
    total: grouped.length,
  });
  const pagedGroups = grouped.slice(shownOffset, shownOffset + CATEGORIES_PER_PAGE);

  // A category card caps at ROWS_PER_CATEGORY rows, with a "+N more" link to
  // reveal the rest in place — keeps every card roughly the same height
  // regardless of how large its category is (Tax & Compliance has 10+
  // entries; most have 2-4).
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

  const rateMutation = useMutation({
    mutationFn: ({ skillId, level }: { skillId: string; level: number }) =>
      usersApi.rateSkill(skillId, { level }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userSkills.mine() });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed to save rating'),
  });

  const certMutation = useMutation({
    mutationFn: ({
      skillId,
      certificationName,
      certificationExpiresAt,
    }: {
      skillId: string;
      certificationName: string | null;
      certificationExpiresAt: string | null;
    }) => usersApi.rateSkill(skillId, { certificationName, certificationExpiresAt }),
    onSuccess: () => {
      toast.success('Certification saved');
      queryClient.invalidateQueries({ queryKey: queryKeys.userSkills.mine() });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Failed to save certification'),
  });

  const [newSkillName, setNewSkillName] = useState('');
  // A request for something already in the catalog (ignoring case — "stripe"
  // is "Stripe") is caught here, pointing the employee at the existing entry
  // instead of queueing a duplicate for review. The server refuses it too
  // (and also catches deactivated entries, which aren't in `rows`).
  const duplicateOf = useMemo(() => {
    const wanted = newSkillName.trim().toLowerCase();
    if (!wanted) return undefined;
    return rows.find((r) => r.skill.name.toLowerCase() === wanted)?.skill;
  }, [newSkillName, rows]);
  const [newSkillCategory, setNewSkillCategory] = useState('');
  const [newSkillNote, setNewSkillNote] = useState('');
  const submitMutation = useMutation({
    mutationFn: () =>
      skillSubmissionsApi.submit({
        proposedName: newSkillName.trim(),
        suggestedCategory: newSkillCategory.trim() || null,
        note: newSkillNote.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Submitted for review');
      setNewSkillName('');
      setNewSkillCategory('');
      setNewSkillNote('');
      setRequestOpen(false);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Submit failed'),
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setScaleOpen(true)}>
          <Info className="h-4 w-4" />
          Proficiency scale
        </Button>
        {readOnly ? null : (
          <Button variant="outline" size="sm" onClick={() => setRequestOpen(true)}>
            <Plus className="h-4 w-4" />
            Request New Skill
          </Button>
        )}
      </div>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <SearchInput
          placeholder="Search skills…"
          value={searchInput}
          onValueChange={(v) => {
            setSearchInput(v);
          }}
        />
        <Select
          value={category ?? 'all'}
          onValueChange={(v) => onSearchChange({ category: v === 'all' ? undefined : v })}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Switch
            id="unrated-only"
            checked={unratedOnly}
            onCheckedChange={(checked) => onSearchChange({ unrated: checked ? true : undefined })}
          />
          <Label htmlFor="unrated-only" className="text-sm font-normal text-muted-foreground">
            Show unrated only
          </Label>
        </div>
      </div>

      {query.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={`skeleton-${i}`} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      ) : query.isError ? (
        <div className="rounded-md border bg-card py-10 text-center text-destructive">
          {query.error instanceof ApiError ? query.error.message : 'Failed to load.'}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border bg-card py-10 text-center text-muted-foreground">
          {q || category || unratedOnly
            ? 'No skills match these filters.'
            : 'No skills in the catalog yet.'}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {pagedGroups.map(([cat, catRows]) => {
              const ratedCount = catRows.filter((r) => !isUnrated(r)).length;
              const expanded = expandedCats.has(cat);
              const visibleRows = expanded ? catRows : catRows.slice(0, ROWS_PER_CATEGORY);
              const hiddenCount = catRows.length - visibleRows.length;
              return (
                <div key={cat} className="overflow-hidden rounded-lg border bg-card">
                  <div className="flex items-center gap-2 border-b bg-surface-subtle px-4 py-3">
                    <span className="text-sm font-semibold">{cat}</span>
                    <Badge variant="secondary" className="ml-auto text-[10px]">
                      {ratedCount}/{catRows.length} rated
                    </Badge>
                  </div>
                  <div className="divide-y px-4">
                    {visibleRows.map((r) => (
                      <SkillRatingRow
                        key={r.skill.id}
                        row={r}
                        readOnly={readOnly}
                        ownerUserId={ownerUserId}
                        onRate={(level) => rateMutation.mutate({ skillId: r.skill.id, level })}
                        onSaveCert={(name, expires) =>
                          certMutation.mutate({
                            skillId: r.skill.id,
                            certificationName: name,
                            certificationExpiresAt: expires,
                          })
                        }
                        saving={
                          rateMutation.isPending && rateMutation.variables?.skillId === r.skill.id
                        }
                      />
                    ))}
                  </div>
                  {hiddenCount > 0 ? (
                    <button
                      type="button"
                      className="block w-full px-4 py-2 text-left text-xs font-medium text-primary hover:underline"
                      onClick={() => setExpandedCats((prev) => new Set(prev).add(cat))}
                    >
                      +{hiddenCount} more
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          <PaginationFooter
            shownOffset={shownOffset}
            shownCount={pagedGroups.length}
            total={grouped.length}
            pageSize={CATEGORIES_PER_PAGE}
            page={catPage}
            pageCount={pageCount}
            isFetching={false}
            onPrevious={() => setCatPage((p) => Math.max(0, p - 1))}
            onNext={() => setCatPage((p) => p + 1)}
          />
        </>
      )}

      <Dialog open={scaleOpen} onOpenChange={setScaleOpen}>
        {/* Capped height + `overflow-hidden` on the outer box, with the scroll
            living on the inner div instead: the close button is DialogContent's
            own absolutely-positioned child, so if DialogContent itself scrolled
            the button would scroll out of view with it on tall content — this
            keeps the button fixed in the corner while only the body scrolls. */}
        <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-2xl">
          {/* ProficiencyScaleReference renders its own heading + description —
              this title is for screen readers only, so it isn't duplicated
              visually. */}
          <DialogTitle className="sr-only">Proficiency scale reference</DialogTitle>
          <div className="overflow-y-auto p-6">
            <ProficiencyScaleReference />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a new skill or application</DialogTitle>
            <DialogDescription>
              Not in the catalog? Submit it here — it's routed to the skill catalog admin for
              review, so the master list stays clean instead of everyone free-typing their own
              version.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="new-skill-name">Skill or application</Label>
              <Input
                id="new-skill-name"
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                placeholder="e.g. Zoho Books"
                aria-describedby={duplicateOf ? 'new-skill-duplicate' : undefined}
              />
              {duplicateOf ? (
                <p id="new-skill-duplicate" className="text-xs text-muted-foreground">
                  Already in the catalog as "{duplicateOf.name}".{' '}
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => {
                      // Jump to it: search for it with every other filter cleared.
                      setSearchInput(duplicateOf.name);
                      onSearchChange({
                        q: duplicateOf.name,
                        category: undefined,
                        unrated: undefined,
                      });
                      setRequestOpen(false);
                      setNewSkillName('');
                    }}
                  >
                    Show it
                  </button>
                </p>
              ) : null}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="new-skill-category">Suggested category</Label>
              <Input
                id="new-skill-category"
                value={newSkillCategory}
                onChange={(e) => setNewSkillCategory(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="new-skill-note">Note for reviewer</Label>
              <Input
                id="new-skill-note"
                value={newSkillNote}
                onChange={(e) => setNewSkillNote(e.target.value)}
                placeholder="Optional — where you've used it, why it's relevant"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                newSkillName.trim().length === 0 ||
                duplicateOf !== undefined ||
                submitMutation.isPending
              }
              onClick={() => submitMutation.mutate()}
            >
              Submit for review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * 5-star proficiency picker. Levels 1-5 map one-to-one onto the 5 stars;
 * level 0 ("No experience") has no star of its own — clicking the
 * currently-selected star again clears back to 0, which is the only way to
 * reach it (mirrors how a star rating can't normally select "zero stars"
 * directly). Hovering a star previews its label without committing a rating;
 * `onChange` only fires on click.
 */
function ProficiencyStars({
  value,
  levelLabels,
  onChange,
  disabled,
  readOnly,
}: {
  value: number | null;
  levelLabels: Record<number, string>;
  onChange: (level: number) => void;
  disabled: boolean;
  readOnly: boolean;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const displayLevel = hovered ?? value ?? 0;

  if (readOnly) {
    const label = value ? `${value} – ${levelLabels[value]}` : 'Not rated';
    return (
      <div className="flex items-center gap-0.5" role="img" aria-label={label} title={label}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={cn(
              'm-0.5 h-4 w-4',
              i <= (value ?? 0)
                ? 'fill-amber-400 text-amber-400 dark:fill-amber-300 dark:text-amber-300'
                : 'fill-none text-muted-foreground',
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Tooltip key={i}>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-pressed={value === i}
              aria-label={`${i} – ${levelLabels[i]}`}
              disabled={disabled}
              className="p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onChange(value === i ? 0 : i)}
            >
              <Star
                className={cn(
                  'h-4 w-4 transition-colors',
                  i <= displayLevel
                    ? 'fill-amber-400 text-amber-400 dark:fill-amber-300 dark:text-amber-300'
                    : 'fill-none text-muted-foreground',
                )}
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {i} – {levelLabels[i]}
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

function SkillRatingRow({
  row,
  readOnly,
  ownerUserId,
  onRate,
  onSaveCert,
  saving,
}: {
  row: MyRatedSkill;
  readOnly: boolean;
  /** Whose rating this is — the certificate download's target. */
  ownerUserId: string | undefined;
  onRate: (level: number) => void;
  onSaveCert: (name: string | null, expires: string | null) => void;
  saving: boolean;
}) {
  const { skill, rating } = row;
  const levelLabels = LEVEL_LABELS[skill.type];

  const [certOpen, setCertOpen] = useState(false);
  const [certName, setCertName] = useState(rating?.certificationName ?? '');
  const [certExpires, setCertExpires] = useState(rating?.certificationExpiresAt ?? '');

  const certFile = rating?.certificateFile ?? null;
  // Here, not in the popover: an upload outlives the popover closing.
  const certActions = useCertificateFileActions(skill.id);
  const hasCert = Boolean(rating?.certificationName) || certFile !== null;
  const certTitle = hasCert
    ? `${rating?.certificationName ?? certFile?.filename}${rating?.certificationExpiresAt ? ` · Exp ${new Date(rating.certificationExpiresAt).toLocaleDateString()}` : ''}`
    : 'Add certificate';

  return (
    <div className="flex items-center gap-2 py-2">
      <span className="min-w-0 flex-1 truncate text-sm" title={skill.name}>
        {skill.name}
      </span>
      <ProficiencyStars
        value={rating?.proficiencyLevel ?? null}
        levelLabels={levelLabels}
        onChange={onRate}
        disabled={saving}
        readOnly={readOnly}
      />
      {readOnly && certFile && ownerUserId ? (
        // Read-only with a file: the paperclip becomes a download.
        <CertificateDownloadButton
          userId={ownerUserId}
          skillId={skill.id}
          filename={certFile.filename}
          details={rating?.certificationName ? certTitle : undefined}
        />
      ) : readOnly ? (
        // Read-only: the certification is shown, not editable. The icon slot
        // stays so rows line up whether or not a cert is present.
        <span
          className={cn(
            'inline-flex size-8 shrink-0 items-center justify-center',
            hasCert ? 'text-accent-foreground' : 'invisible',
          )}
          title={hasCert ? certTitle : undefined}
          aria-hidden={!hasCert}
        >
          <Paperclip className="h-3.5 w-3.5" aria-hidden />
          {hasCert ? <span className="sr-only">{certTitle}</span> : null}
        </span>
      ) : (
        <Popover open={certOpen} onOpenChange={setCertOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className={cn(
                'shrink-0',
                hasCert ? 'text-accent-foreground' : 'text-muted-foreground',
              )}
              title={certTitle}
              aria-label={certTitle}
            >
              {certActions.uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Paperclip className="h-3.5 w-3.5" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 space-y-3">
            <div className="grid gap-1.5">
              <Label htmlFor={`cert-name-${skill.id}`}>Certification</Label>
              <Input
                id={`cert-name-${skill.id}`}
                value={certName}
                onChange={(e) => setCertName(e.target.value)}
                placeholder="e.g. ProAdvisor"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`cert-exp-${skill.id}`}>Expires</Label>
              <Input
                id={`cert-exp-${skill.id}`}
                type="date"
                value={certExpires}
                onChange={(e) => setCertExpires(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              className="w-full"
              onClick={() => {
                onSaveCert(certName.trim() || null, certExpires || null);
                setCertOpen(false);
              }}
            >
              Save
            </Button>
            {ownerUserId ? (
              <div className="border-t pt-3">
                <CertificateFileEditor
                  ownerUserId={ownerUserId}
                  skillId={skill.id}
                  file={certFile}
                  actions={certActions}
                />
              </div>
            ) : null}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
